import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connectStore } from 'redux-box';
import { module as wordgame } from '@st/wordgame';
import { module as ui } from '@st/ui';
import { ThemeButton } from '@sc/custom.styled';
import Select, { components } from 'react-select';
import wordsInfoApi from 'services/api/wordsinfo';
import gradeApi from 'services/api/grade';
import useDebouncing from '@u/useDebouncing';

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    position: "absolute",
  }),
  control: (provide) => ({
    ...provide,
    width: "100%",
    margin: "0 auto",
  }),
};

const CustomMenuList = (props) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.isLoadingMore && (
        <div style={{ textAlign: "center", padding: "8px", color: "#999", fontSize: 12 }}>
          Loading more…
        </div>
      )}
    </components.MenuList>
  );
};

function WordGameFormModal({ wordgame, ui }) {
  const editData = window.wordGameEditData || null;
  const [selectedWord, setSelectedWord] = useState(null);
  const [level, setLevel] = useState('Easy');
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  const [itemOptions, setItemOptions] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemOffset, setItemOffset] = useState(0);
  const [itemHasNext, setItemHasNext] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemLoadingMore, setItemLoadingMore] = useState(false);

  const debouncedItemSearch = useDebouncing(itemSearch, 400);

  useEffect(() => {
    gradeApi.getAllGrades().then((res) => {
      const gOptions = (res || []).map(g => ({ label: g.name, value: g.id }));
      setGradeOptions(gOptions);
    }).catch(err => console.error(err));
  }, []);

  const fetchWords = useCallback(
    async ({ search = "", offset = 0, append = false }) => {
      append ? setItemLoadingMore(true) : setItemLoading(true);
      try {
        const data = await wordsInfoApi.getWordsInfo(offset, search, '');
        const newOptions = (data.result || []).map(w => ({ label: w.word, value: w.id }));
        setItemOptions(prev => (append ? [...prev, ...newOptions] : newOptions));
        setItemOffset(offset);
        setItemHasNext(!!data.has_next);
      } catch (err) {
        console.error(err);
      } finally {
        append ? setItemLoadingMore(false) : setItemLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchWords({ search: debouncedItemSearch, offset: 0, append: false });
  }, [debouncedItemSearch, fetchWords]);

  const loadMoreItems = useCallback(() => {
    if (itemHasNext && !itemLoadingMore && !itemLoading) {
      const nextOffset = itemOffset + 1;
      fetchWords({ search: debouncedItemSearch, offset: nextOffset, append: true });
    }
  }, [itemHasNext, itemLoadingMore, itemLoading, itemOffset, debouncedItemSearch, fetchWords]);

  const handleItemSearchInput = (inputValue, { action }) => {
    setItemSearch(inputValue);
  };

  useEffect(() => {
    if (editData) {
      if (editData.word_id && editData.word_id.word) {
        setSelectedWord({ label: editData.word_id.word, value: editData.word_id.id || editData.word_id.word });
      }
      setLevel(editData.word_level || 'Easy');

      if (editData.grade && Array.isArray(editData.grade)) {
        const selectedG = editData.grade.map(g => {
          const matched = gradeOptions.find(opt => opt.value === g);
          return matched ? matched : { label: "Unknown Grade", value: g };
        });
        setSelectedGrade(selectedG);
      } else {
        setSelectedGrade([]);
      }
    } else {
      setSelectedWord(null);
      setLevel('Easy');
      setSelectedGrade([]);
    }
  }, [editData, gradeOptions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedWord) {
      alert("Please select a word.");
      return;
    }
    if (!selectedGrade || selectedGrade.length === 0) {
      alert("Please select at least one grade.");
      return;
    }
    const payload = {
      word_id: selectedWord.value,
      word_level: level,
      grade: selectedGrade.map(g => g.value)
    };
    if (editData) {
      wordgame.updateWord(editData.id, payload);
    } else {
      wordgame.createWord(payload);
    }
  };

  return (
    <div style={{ padding: '20px', minWidth: '400px', backgroundColor: '#fff', borderRadius: '8px' }}>
      <h2>{editData ? "Edit Word" : "Add Word"}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Word Text</label>
          <Select
            className="dropdown-container"
            value={selectedWord}
            onChange={setSelectedWord}
            onInputChange={handleItemSearchInput}
            isLoading={itemLoading}
            options={itemOptions}
            filterOption={() => true}
            components={{ MenuList: CustomMenuList }}
            onMenuScrollToBottom={loadMoreItems}
            isLoadingMore={itemLoadingMore}
            placeholder={itemLoading ? "Loading..." : "Search for a word..."}
            styles={customStyles}
            menuPlacement="auto"
            menuPosition="fixed"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Grade</label>
          <Select
            className="dropdown-container"
            isMulti
            options={gradeOptions}
            value={selectedGrade}
            onChange={setSelectedGrade}
            placeholder="Select Grade(s)..."
            styles={customStyles}
            menuPlacement="auto"
            menuPosition="fixed"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Level</label>
          <select
            value={level}
            onChange={e => setLevel(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          >
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
          <ThemeButton type="button" onClick={() => ui.hideModal()}>Cancel</ThemeButton>
          <ThemeButton primary type="submit">Save</ThemeButton>
        </div>
      </form>
    </div>
  );
}

export default connectStore({ wordgame, ui })(WordGameFormModal);
